import React from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { GraduationCap, Briefcase, Award, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const UNIVERSITY_IMG = "https://images.unsplash.com/photo-1631509057887-7ef19853bc39?crop=entropy&cs=srgb&fm=jpg&q=85";

const ProgramsPage = () => {
  const { t } = useLanguage();

  const programs = [
    {
      icon: GraduationCap,
      titleKey: 'program1.title',
      subtitleKey: 'program1.subtitle',
      descriptionKey: 'program1.description',
      featuresKey: 'program1.features',
      color: 'oar-red',
      stats: { students: '10 000', deadline: '2035', universities: '50+' },
    },
    {
      icon: Briefcase,
      titleKey: 'program2.title',
      subtitleKey: 'program2.subtitle',
      descriptionKey: 'program2.description',
      featuresKey: 'program2.features',
      color: 'oar-orange',
      stats: { companies: '200+', investments: '€50M+', countries: '15+' },
    },
    {
      icon: Award,
      titleKey: 'program3.title',
      subtitleKey: 'program3.subtitle',
      descriptionKey: 'program3.description',
      featuresKey: 'program3.features',
      color: 'oar-green',
      stats: { diplomats: '500+', seminars: '25+', embassies: '30+' },
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 bg-oar-blue-dark overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={UNIVERSITY_IMG}
            alt="University Building"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-oar-blue-dark via-oar-blue-dark/90 to-oar-blue-dark" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-oar-orange font-bold">
            Initiatives Stratégiques
          </span>
          <h1
            data-testid="programs-title"
            className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-white mt-4 mb-6"
          >
            {t('programs.title')}
          </h1>
          <p className="max-w-2xl text-lg text-gray-300">
            {t('programs.subtitle')}
          </p>
        </div>
      </section>

      {/* Programs List */}
      <section className="py-24 bg-oar-bg-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-16">
            {programs.map((program, index) => (
              <div
                key={index}
                data-testid={`program-card-${index + 1}`}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div
                    className={`w-14 h-14 flex items-center justify-center rounded-sm mb-6 ${
                      program.color === 'oar-red'
                        ? 'bg-oar-red-soft text-oar-red'
                        : program.color === 'oar-orange'
                        ? 'bg-oar-orange-soft text-oar-orange'
                        : 'bg-oar-green-soft text-oar-green'
                    }`}
                  >
                    <program.icon className="w-7 h-7" />
                  </div>

                  <span className="text-xs uppercase tracking-[0.2em] text-oar-orange font-bold">
                    {t(`programs.${program.subtitleKey}`)}
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-oar-text-primary mt-2 mb-4">
                    {t(`programs.${program.titleKey}`)}
                  </h2>
                  <p className="text-oar-text-secondary leading-relaxed mb-6">
                    {t(`programs.${program.descriptionKey}`)}
                  </p>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {t(`programs.${program.featuresKey}`).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-oar-green flex-shrink-0" />
                        <span className="text-oar-text-secondary text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    className="group inline-flex items-center gap-2 text-oar-blue hover:text-oar-blue-light transition-colors font-medium"
                  >
                    {t('common.learnMore')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>

                {/* Stats Card */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="bg-white border border-oar-border rounded-lg p-8 shadow-sm">
                    <div className="grid grid-cols-3 gap-6">
                      {Object.entries(program.stats).map(([key, value], idx) => (
                        <div key={idx} className="text-center">
                          <div className="text-2xl md:text-3xl font-heading font-bold text-oar-blue">
                            {value}
                          </div>
                          <div className="text-xs text-oar-text-muted uppercase tracking-wider mt-2">
                            {key === 'students' && 'Étudiants'}
                            {key === 'deadline' && 'Horizon'}
                            {key === 'universities' && 'Universités'}
                            {key === 'companies' && 'Entreprises'}
                            {key === 'investments' && 'Investissements'}
                            {key === 'countries' && 'Pays'}
                            {key === 'diplomats' && 'Diplomates'}
                            {key === 'seminars' && 'Séminaires'}
                            {key === 'embassies' && 'Ambassades'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-oar-blue-dark">
        <div className="africa-line" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 pt-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            {t('programs.cta.title')}
          </h2>
          <p className="text-gray-300 text-lg mb-10">
            {t('programs.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/apply"
              data-testid="apply-program-btn"
              className="inline-flex items-center justify-center gap-2 bg-oar-red hover:bg-oar-red-dark text-white px-8 py-4 rounded-sm font-medium transition-all duration-300"
            >
              <GraduationCap className="w-5 h-5" />
              {t('programs.cta.applyBtn')}
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded-sm font-medium transition-all duration-300"
            >
              {t('programs.cta.contactBtn')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProgramsPage;
